import React from 'react'
import { InferType } from 'yup'
import { captureException } from '@sentry/react'
import { useMutation } from '@tanstack/react-query'
import { Modal } from '../../../common/Modal'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { ToastType } from '../../../common/Toast/toast-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { CreateUpdateAdminForm } from '../CreateUpdateAdminForm'
import { societyAdminCreateSchema } from '../../../../forms/schemas/society_admin_create'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { addAdminUsers } from '../../../../api/societies'

interface CreateUpdateAdminModalProps {
  show: boolean
  close: () => void
  children?: React.ReactNode
}

export const CreateUpdateAdminModal = ({
  children,
  show,
  close,
}: CreateUpdateAdminModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { society, isLoading: isLoadingSociety } = useCurrentSociety()
  const { setToastNotification } = useToastNotifications()

  const showErrorMessage = (isPlural: boolean): void => {
    const message = isPlural
      ? translate('createUpdateAdminModal.flashMessage.adminAddFailurePlural')
      : translate('createUpdateAdminModal.flashMessage.adminAddFailureSingular')
    setToastNotification(ToastType.DANGER, message)
  }

  const showSuccessMessage = (isPlural: boolean): void => {
    const message = isPlural
      ? translate('createUpdateAdminModal.flashMessage.adminAddSuccessPlural')
      : translate('createUpdateAdminModal.flashMessage.adminAddSuccessSingular')
    setToastNotification(ToastType.SUCCESS, message)
  }

  const { mutate: mutateAddAdminUsers, isPending: isAddingAdmins } =
    useMutation({
      mutationFn: addAdminUsers,
      onSuccess: (_data, variables) => {
        showSuccessMessage(variables.userIds.length > 1)
        close()
      },
      onError: (error, variables) => {
        showErrorMessage(variables.userIds.length > 1)
        captureException(error)
      },
    })

  const onSubmit = async (
    data: InferType<typeof societyAdminCreateSchema>
  ): Promise<void> => {
    const selectedUserIds = data.userIds
      .map((_userId) => _userId)
      .filter((_userId) => _userId !== undefined) as string[]
    society &&
      mutateAddAdminUsers({
        id: society._id,
        userIds: selectedUserIds,
      })
  }

  const onError = (error: unknown): void => {
    captureException(error)
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  const loading = isLoadingSociety || isAddingAdmins

  return (
    <>
      {children}
      <Modal show={show} size="md" minHeight={400}>
        {{
          header: (
            <ModalHeader onClose={close}>
              {translate('createUpdateAdminModal.addNewAdminTitle')}
            </ModalHeader>
          ),
          body: (
            <ModalBody className="justify-between">
              {society && (
                <CreateUpdateAdminForm
                  society={society}
                  onError={onError}
                  onSubmit={onSubmit}
                  onClose={close}
                  loading={loading}
                />
              )}
            </ModalBody>
          ),
        }}
      </Modal>
    </>
  )
}
